import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Loader } from "lucide-react";
import { useAuth } from "@/components/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { getProductionTimeline } from "@/integrations/supabase/production-timeline";
import html2pdf from "html2pdf.js";
import { useState, useEffect, useRef } from "react";

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const isUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export default function PedidosImpressao() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { empresa } = useAuth();
  const [timeline, setTimeline] = useState<any>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const pdfContentRef = useRef<HTMLDivElement>(null);

  const { data: orderData, isLoading, error } = useQuery({
    queryKey: ["orderPrint", id],
    queryFn: async () => {
      if (!id) throw new Error("ID do pedido não fornecido");

      const uuidField = isUUID(id.trim()) ? "id" : "code";

      const { data: order, error: orderError } = await supabase
        .from("atelie_orders")
        .select("*")
        .eq(uuidField, id)
        .maybeSingle();

      if (orderError || !order) throw new Error("Pedido não encontrado");

      const { data: personalizations } = await supabase
        .from("order_personalizations")
        .select("*")
        .eq("order_id", order.id);

      return { order, personalizations: personalizations || [] };
    },
    enabled: !!id && !!empresa?.id,
    retry: 3,
    staleTime: 0
  });

  // Carregar timeline da produção
  useEffect(() => {
    const loadTimeline = async () => {
      if (!orderData?.order?.id) return;

      try {
        const timelineData = await getProductionTimeline(orderData.order.id);
        setTimeline(timelineData);
      } catch (error) {
        console.warn('Erro ao carregar timeline:', error);
        setTimeline(null);
      }
    };

    loadTimeline();
  }, [orderData?.order?.id]);

  const generatePDF = async () => {
    if (!pdfContentRef.current) return;

    setIsGeneratingPDF(true);
    try {
      const element = pdfContentRef.current;
      const filename = `Pedido_${orderData?.order?.code || 'N-A'}_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.pdf`;

      const opt = {
        margin: 10,
        filename: filename,
        image: { type: 'jpeg' as any, quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { orientation: 'portrait' as any, unit: 'mm', format: 'a4' }
      };

      html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !orderData) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="flex items-center gap-4 p-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/pedidos")}
              className="border-gray-300"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Erro ao carregar pedido
              </h1>
            </div>
          </div>
        </header>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <button
            onClick={() => navigate("/pedidos")}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Voltar para Pedidos
          </button>
        </div>
      </div>
    );
  }

  const order = orderData.order;
  const personalizations = orderData.personalizations || [];

  // Determinar o status a exibir (timeline ou padrão)
  const getCurrentTimelineStatus = (): string => {
    if (!timeline || !timeline.steps || timeline.steps.length === 0 || !timeline.current_step_key) {
      return order?.status || 'Aguardando aprovação';
    }

    const currentStep = timeline.steps.find((s: any) => s.key === timeline.current_step_key);
    return currentStep?.label || order?.status || 'Aguardando aprovação';
  };

  const safeOrder = {
    code: String(order?.code || 'N/A'),
    customer_name: String(order?.customer_name || 'Cliente não informado'),
    type: String(order?.type || 'Produto'),
    description: String(order?.description || 'Sem descrição'),
    value: Number(order?.value || 0),
    paid: Number(order?.paid || 0),
    discount: Number(order?.discount || 0),
    discount_type: String(order?.discount_type || 'fixed'),
    status: getCurrentTimelineStatus(),
    delivery_date: String(order?.delivery_date || 'A combinar'),
    created_at: String(order?.created_at || new Date().toISOString())
  };

  const safePersonalizations = (personalizations || []).map((item: any) => ({
    person_name: String(item?.person_name || "Cliente"),
    size: item?.size ? String(item.size) : "—",
    quantity: Number(item?.quantity || 1),
    notes: item?.notes ? String(item.notes) : "",
  }));

  const descountAmount = safeOrder.discount_type === 'percentage'
    ? (safeOrder.value * safeOrder.discount) / 100
    : safeOrder.discount;

  const valorComDesconto = safeOrder.value - descountAmount;
  const saldo = valorComDesconto - safeOrder.paid;

  const statusConfig = {
    "Aguardando aprovação": { bg: "bg-yellow-50", border: "border-yellow-300", text: "text-yellow-900", badge: "🔴" },
    "Em produção": { bg: "bg-blue-50", border: "border-blue-300", text: "text-blue-900", badge: "🔵" },
    "Finalizando": { bg: "bg-purple-50", border: "border-purple-300", text: "text-purple-900", badge: "🟣" },
    "Pronto": { bg: "bg-green-50", border: "border-green-300", text: "text-green-900", badge: "✅" },
    "Aguardando retirada": { bg: "bg-green-50", border: "border-green-300", text: "text-green-900", badge: "✅" },
    "Entregue": { bg: "bg-gray-50", border: "border-gray-300", text: "text-gray-900", badge: "📦" },
    "Cancelado": { bg: "bg-red-50", border: "border-red-300", text: "text-red-900", badge: "❌" }
  }[safeOrder.status] || { bg: "bg-gray-50", border: "border-gray-300", text: "text-gray-900", badge: "•" };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex items-center gap-4 p-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/pedidos")}
            className="border-gray-300"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">Pedido para Impressão</h1>
            <p className="text-gray-600 text-sm">
              Código: {safeOrder.code} | Cliente: {safeOrder.customer_name}
            </p>
          </div>
          <Button
            onClick={generatePDF}
            disabled={isGeneratingPDF}
            className="bg-blue-600 hover:bg-blue-700 gap-2"
          >
            {isGeneratingPDF ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Gerando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Baixar PDF
              </>
            )}
          </Button>
        </div>
      </header>

      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8" ref={pdfContentRef}>
          {/* Cabeçalho com Logo */}
          <div className="border-b-2 border-gray-300 pb-6 mb-6">
            {/* Logo e Título em uma linha */}
            <div className="flex justify-between items-start gap-6 mb-6">
              {/* Logo da Empresa */}
              <div className="flex-shrink-0">
                {empresa?.logo_url ? (
                  <img
                    src={empresa.logo_url}
                    alt={empresa?.nome}
                    className="h-12 object-contain"
                  />
                ) : (
                  <div className="text-lg font-bold text-gray-900">{empresa?.nome || 'Ateliê Pro'}</div>
                )}
              </div>

              {/* Título compacto - Centro */}
              <div className="flex-1 text-center">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-widest mb-1">Número do Pedido</p>
                <h1 className="text-2xl font-bold text-gray-900">#{safeOrder.code}</h1>
              </div>

              {/* Status - Direita */}
              <div className="flex-shrink-0">
                <div className={`px-3 py-2 rounded-lg border-2 font-bold text-xs ${statusConfig.bg} ${statusConfig.border} ${statusConfig.text} whitespace-nowrap`}>
                  {statusConfig.badge} {safeOrder.status}
                </div>
              </div>
            </div>

            {/* Datas em grid compacto */}
            <div className="grid grid-cols-3 gap-4 text-xs">
              <div className="bg-gray-50 p-2 rounded">
                <p className="text-gray-500 font-medium uppercase tracking-wide">Data do Pedido</p>
                <p className="text-gray-900 font-bold text-sm">{new Date(safeOrder.created_at).toLocaleDateString('pt-BR')}</p>
              </div>
              <div className="bg-gray-50 p-2 rounded">
                <p className="text-gray-500 font-medium uppercase tracking-wide">Entrega</p>
                <p className="text-gray-900 font-bold text-sm">
                  {safeOrder.delivery_date === 'A combinar'
                    ? 'A combinar'
                    : new Date(safeOrder.delivery_date).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div className="bg-gray-50 p-2 rounded">
                <p className="text-gray-500 font-medium uppercase tracking-wide">Tipo</p>
                <p className="text-gray-900 font-bold text-sm">{safeOrder.type}</p>
              </div>
            </div>
          </div>

          {/* Informações do Cliente */}
          <div className="border-l-4 border-purple-500 pl-4 py-2 mb-6 bg-purple-50">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-0.5">Cliente</p>
            <p className="text-lg font-bold text-gray-900">{safeOrder.customer_name}</p>
          </div>

          {/* Seção de Conserto (se aplicável) */}
          {orderData?.order?.conserto_dados && (
            <div className="border-l-4 border-yellow-500 pl-4 py-2 mb-6 bg-yellow-50">
              <p className="text-xs font-semibold text-yellow-900 uppercase tracking-widest mb-2">🔧 Detalhes do Conserto</p>
              <div className="space-y-1 text-xs">
                {orderData.order.conserto_dados.materialCliente && (
                  <div className="flex gap-2">
                    <span className="font-medium text-yellow-900">Peça do cliente:</span>
                    <span className="text-gray-700">Sim</span>
                  </div>
                )}
                {orderData.order.conserto_dados.tipoDefeito && (
                  <div className="flex gap-2">
                    <span className="font-medium text-yellow-900">Tipo de defeito:</span>
                    <span className="text-gray-700 capitalize">{orderData.order.conserto_dados.tipoDefeito.replace(/_/g, ' ')}</span>
                  </div>
                )}
                {orderData.order.conserto_dados.areiaAfetada && (
                  <div className="flex gap-2">
                    <span className="font-medium text-yellow-900">Área afetada:</span>
                    <span className="text-gray-700">{orderData.order.conserto_dados.areiaAfetada}</span>
                  </div>
                )}
                {orderData.order.conserto_dados.nivelDificuldade && (
                  <div className="flex gap-2">
                    <span className="font-medium text-yellow-900">Dificuldade:</span>
                    <span className="text-gray-700 capitalize">{orderData.order.conserto_dados.nivelDificuldade}</span>
                  </div>
                )}
                {orderData.order.conserto_dados.descricaoDefeito && (
                  <div>
                    <p className="font-medium text-yellow-900 mb-1">Descrição do defeito:</p>
                    <p className="text-gray-700 bg-white p-2 rounded border border-yellow-200 italic">{orderData.order.conserto_dados.descricaoDefeito}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Seção de Prova (se aplicável) */}
          {orderData?.order?.prova_dados && (
            <div className="border-l-4 border-green-500 pl-4 py-2 mb-6 bg-green-50">
              <p className="text-xs font-semibold text-green-900 uppercase tracking-widest mb-2">📅 Prova Agendada</p>
              <div className="space-y-1 text-xs">
                {orderData.order.prova_dados.data_prova && (
                  <div className="flex gap-2">
                    <span className="font-medium text-green-900">Data:</span>
                    <span className="text-gray-700">{new Date(orderData.order.prova_dados.data_prova).toLocaleDateString('pt-BR')}</span>
                  </div>
                )}
                {orderData.order.prova_dados.hora_prova && (
                  <div className="flex gap-2">
                    <span className="font-medium text-green-900">Hora:</span>
                    <span className="text-gray-700">{orderData.order.prova_dados.hora_prova}</span>
                  </div>
                )}
                {orderData.order.prova_dados.local_prova && (
                  <div className="flex gap-2">
                    <span className="font-medium text-green-900">Local:</span>
                    <span className="text-gray-700">{orderData.order.prova_dados.local_prova}</span>
                  </div>
                )}
                {orderData.order.prova_dados.observacoes && (
                  <div>
                    <p className="font-medium text-green-900 mb-1">Observações:</p>
                    <p className="text-gray-700 bg-white p-2 rounded border border-green-200 italic">{orderData.order.prova_dados.observacoes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Descrição e Itens em Tabela */}
          <div className="mb-6">
            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide mb-2 pb-1 border-b border-gray-300">Produtos Solicitados</h3>
            {safeOrder.description && safeOrder.description !== 'Sem descrição' ? (
              <div className="bg-gray-50 p-3 rounded border border-gray-200 text-sm text-gray-800 leading-relaxed">
                {safeOrder.description}
              </div>
            ) : (
              <div className="text-xs text-gray-500 italic p-2">Nenhuma descrição</div>
            )}
          </div>

          {/* Personalizações */}
          {safePersonalizations.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide mb-2 pb-1 border-b border-gray-300">Detalhes dos Itens</h3>
              <div className="overflow-hidden border border-gray-200 rounded">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100 border-b border-gray-300">
                    <tr>
                      <th className="px-3 py-2 text-left font-bold text-gray-900">PESSOA</th>
                      <th className="px-3 py-2 text-left font-bold text-gray-900">TAMANHO</th>
                      <th className="px-3 py-2 text-center font-bold text-gray-900">QTD</th>
                      <th className="px-3 py-2 text-left font-bold text-gray-900">OBSERVAÇÕES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {safePersonalizations.map((item, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2 font-semibold text-gray-900">{item.person_name}</td>
                        <td className="px-3 py-2 text-gray-700">{item.size}</td>
                        <td className="px-3 py-2 text-center font-semibold text-gray-900">{item.quantity}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs">{item.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Resumo Financeiro Destaque */}
          <div className="border-2 border-purple-300 rounded-lg p-4 mb-6 bg-gradient-to-r from-purple-50 to-pink-50">
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-700 font-medium">Valor Total:</span>
                <span className="font-bold text-gray-900">{formatCurrency(safeOrder.value)}</span>
              </div>

              {safeOrder.discount > 0 && (
                <div className="flex justify-between text-red-600 bg-red-50 p-1.5 rounded">
                  <span className="font-medium">Desconto ({safeOrder.discount_type === 'percentage' ? `${safeOrder.discount.toFixed(2)}%` : 'R$'}):</span>
                  <span className="font-bold">-{formatCurrency(descountAmount)}</span>
                </div>
              )}

              {safeOrder.discount > 0 && (
                <div className="flex justify-between border-t-2 border-gray-300 pt-1.5">
                  <span className="text-gray-700 font-medium">Valor Final:</span>
                  <span className="font-bold text-gray-900">{formatCurrency(valorComDesconto)}</span>
                </div>
              )}

              {safeOrder.paid > 0 && (
                <div className="flex justify-between text-green-700 bg-green-50 p-1.5 rounded">
                  <span className="font-medium">Já Pago:</span>
                  <span className="font-bold">{formatCurrency(safeOrder.paid)}</span>
                </div>
              )}

              <div className="border-t-2 border-purple-400 pt-1.5 flex justify-between bg-purple-100 p-2 rounded">
                <span className="text-gray-900 font-bold">SALDO:</span>
                <span className={`font-bold text-lg ${saldo > 0 ? 'text-red-700' : 'text-green-700'}`}>
                  {formatCurrency(saldo)}
                </span>
              </div>
            </div>
          </div>

          {/* Contato e Informações */}
          <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-4">
            <div className="text-center space-y-0.5">
              <p className="text-xs font-bold text-gray-900">{empresa?.nome || 'Ateliê Pro'}</p>
              {empresa?.email && <p className="text-xs text-gray-600">📧 {empresa.email}</p>}
              {empresa?.telefone && <p className="text-xs text-gray-600">📱 {empresa.telefone}</p>}
              {empresa?.endereco && <p className="text-xs text-gray-600">📍 {empresa.endereco}</p>}
            </div>
          </div>

          {/* Assinatura e Rodapé */}
          <div className="border-t-2 border-gray-300 pt-4 text-center space-y-3">
            <p className="text-xs text-gray-600 italic">Obrigado pela preferência!</p>

            <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-300">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-5">Assinatura do Cliente</p>
                <div className="h-10"></div>
                <p className="text-xs text-gray-600 font-medium mt-1">_____________________</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-5">Assinatura da Empresa</p>
                <div className="h-10"></div>
                <p className="text-xs text-gray-600 font-medium mt-1">_____________________</p>
              </div>
            </div>

            <div className="pt-3 border-t border-gray-300 bg-gray-100 p-2 rounded text-xs text-gray-600">
              <p className="font-medium text-gray-700">Gerado em {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
